import React, {
  useState,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';

import FirebaseContext from '../../../../../context/FirebaseContext';
import { sendLeadMessage as sendContract } from '../../../../utils/message';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useToast from '../../../../hooks/useToast';
import DialogModal from '../../../../../components/DialogModal';
import { Title } from '../../../../../components/v2/Header';
import InputLabel from '../../../../../components/v2/InputLabel';
import LoadingOverlay from '../../../LoadingOverlay';
import RadioButtonGroup from '../../../RadioButtonGroup';
import { ContactOptions } from '../../../../Model/Lead';

import TemplateSection from './TemplateSection';
import { DEFAULT_MESSAGE_BODY, DEFAULT_EMAIL_SUBJECT } from './utils';
import {
  ModalContent,
  SelectContainer,
  StyledButton,
} from './styles';
import texts from './texts';

const SendOnboardingLinkModal = ({
  recipient,
  onboardingLink,
  showModal,
  onClose,
}) => {
  const {
    leadId,
    name,
    email,
    phoneNumber,
    coachName,
  } = recipient;

  const [selectedOption, setSelectedOption] = useState(ContactOptions.EMAIL);
  const [msgBody, setMsgBody] = useState(format(DEFAULT_MESSAGE_BODY, {
    leadName: name,
    coachName,
    onboardingLink,
  }));

  const [emailSubject, setEmailSubject] = useState(DEFAULT_EMAIL_SUBJECT);
  const [isLoading, setIsLoading] = useState(false);

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { firebase: { remote } } = useContext(FirebaseContext);

  const messageOptions = useMemo(() => Object.values(ContactOptions).map((option) => ({
    label: format(texts.contactOptions[option], { contact: ContactOptions.EMAIL === option ? email : phoneNumber }),
    value: option,
  })), [email, phoneNumber]);

  const sendContractLink = useCallback(async () => {
    if (selectedOption === ContactOptions.EMAIL && !emailSubject) {
      showToast(texts.emailSubjectRequired, { error: true });
      return;
    }
    if (!msgBody) {
      showToast(texts.messageBodyRequired, { error: true });
      return;
    }
    if (!msgBody.includes(onboardingLink)) {
      showToast(texts.onboardingLinkRequired, { error: true });
      return;
    }

    try {
      setIsLoading(true);
      const sendData = {
        msgProvider: selectedOption,
        leadId,
        emailSubject,
        messageBody: msgBody,
      };

      await sendContract(sendData, remote);
      if (isComponentMountedRef.current) {
        showToast(texts.success);
      }
    } catch (error) {
      showToast(format(texts.fail, { error }), { error: true });
    } finally {
      onClose();
      setIsLoading(false);
    }
  }, [
    leadId,
    remote,
    showToast,
    selectedOption,
    onClose,
    isComponentMountedRef,
    emailSubject,
    msgBody,
    onboardingLink,
  ]);

  return (
    <>
      <DialogModal
        open={showModal}
        onClose={onClose}
        maxWidth="lg"
      >
        <ModalContent>
          <Title>{texts.sendOnboardingLink}</Title>
          <SelectContainer>
            <InputLabel>{texts.selectOption}</InputLabel>
            <RadioButtonGroup
              options={messageOptions}
              showStyledOptions={false}
              selectedOption={selectedOption}
              onOptionChange={setSelectedOption}
              isRow
            />
          </SelectContainer>
          <TemplateSection
            showSubject={selectedOption === ContactOptions.EMAIL}
            setEmailSubject={setEmailSubject}
            emailSubject={emailSubject}
            msgBody={msgBody}
            setMsgBody={setMsgBody}
          />
          <StyledButton onClick={sendContractLink}>{texts.sendOnboardingLink}</StyledButton>
        </ModalContent>
      </DialogModal>
      <LoadingOverlay isLoading={isLoading} />
    </>
  );
};

SendOnboardingLinkModal.propTypes = {
  /** Who the link goes to: `{ leadId, name, email, phoneNumber, coachName }`. */
  recipient: PropTypes.object.isRequired,
  onboardingLink: PropTypes.string.isRequired,
  showModal: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SendOnboardingLinkModal;
